if (!customElements.get('mini-variant-picker')) {
  customElements.define(
    'mini-variant-picker',
    class MiniVariantPicker extends HTMLElement {
      constructor() {
        super();
        this.priceTarget = document.getElementById(this.dataset.priceTarget);
        this.badgeTarget = document.getElementById(this.dataset.badgeTarget);
        this.mediaTarget = this.dataset.mediaTarget ? document.getElementById(this.dataset.mediaTarget) : null;
        this.variantIdInput = this.querySelector('.mini-variant-picker__input');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span:not(.sold-out-message)');
        this.soldOutMessage = this.submitButton.querySelector('.sold-out-message');

        // Like juleriaque.com.ar's cards: every variant's image is already in the page
        // (product-card.liquid's <template data-variant-media-id>), and the images of the
        // other sizes are downloaded in the background once the card is on screen, so
        // clicking a size swaps the image instantly. Cache is keyed by media (sizes sharing
        // the same image share one entry) and keeps the image being swapped out, so
        // switching back is instant too.
        this.mediaCache = new Map();
        this.swapToken = 0;
        if (this.mediaTarget) {
          const checked = this.querySelector('.mini-variant-picker__radio:checked');
          this.currentMediaKey = checked ? this.getMediaTemplate(checked.value)?.dataset.mediaKey : null;
          this.observePreload();
        }

        this.addEventListener('change', this.onVariantChange.bind(this));
      }

      disconnectedCallback() {
        this.preloadObserver?.disconnect();
      }

      // Background downloads must never compete with what the visitor is looking at (on a
      // slow connection, ~20 preloads from neighbouring cards made a tapped decant wait
      // 3-10s in the queue). So a card preloads only once it is actually on screen AND its
      // own image has finished loading, at low fetch priority (see product-card.liquid).
      observePreload() {
        if (!('IntersectionObserver' in window)) return;
        this.preloadObserver = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.preloadObserver.disconnect();
          const idle = window.requestIdleCallback || ((callback) => setTimeout(callback, 200));
          this.currentImageLoaded().then(() => idle(() => MiniVariantPicker.enqueuePreload(this)));
        });
        this.preloadObserver.observe(this.mediaTarget);
      }

      currentImageLoaded() {
        const image = this.mediaTarget.querySelector('img');
        if (!image || image.complete) return Promise.resolve();
        return new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        });
      }

      // Cards preload one at a time, so at any moment at most one background download
      // competes with the image the visitor actually taps.
      static preloadQueue = [];
      static preloadRunning = false;

      static enqueuePreload(picker) {
        if (!MiniVariantPicker.preloadQueue.includes(picker)) MiniVariantPicker.preloadQueue.push(picker);
        MiniVariantPicker.runPreloadQueue();
      }

      static async runPreloadQueue() {
        if (MiniVariantPicker.preloadRunning) return;
        MiniVariantPicker.preloadRunning = true;
        while (MiniVariantPicker.preloadQueue.length) {
          // Background preloading pauses while a tapped image is still downloading.
          while (MiniVariantPicker.priorityDownload) await MiniVariantPicker.priorityDownload;
          const picker = MiniVariantPicker.preloadQueue.shift();
          if (picker.isConnected) await picker.preloadAllMedia();
        }
        MiniVariantPicker.preloadRunning = false;
      }

      // Resolves once every image of this card's other sizes has loaded, failed, or been
      // cancelled by prioritize() -- a cancelled image fires neither load nor error, so the
      // entry's own settle() is what keeps the queue from waiting on it forever.
      preloadAllMedia() {
        this.mediaTarget.parentElement
          .querySelectorAll('template[data-variant-media-id]')
          .forEach((template) => this.getCachedMedia(template));
        const waiting = [...MiniVariantPicker.pendingPreloads].filter((entry) => entry.picker === this);
        return Promise.all(waiting.map((entry) => entry.done));
      }

      getMediaTemplate(variantId) {
        return this.mediaTarget?.parentElement.querySelector(`template[data-variant-media-id="${variantId}"]`);
      }

      // Detached copy of a variant's media markup; its images start downloading right
      // away: importNode adopts it into this document (a plain clone of template.content stays
      // in the inert template document and never loads), and detached <img>s with srcset load.
      getCachedMedia(template) {
        const key = template.dataset.mediaKey;
        if (key === this.currentMediaKey) return null;
        if (!this.mediaCache.has(key)) {
          const fragment = document.importNode(template.content, true);
          fragment.querySelectorAll('img').forEach((image) => MiniVariantPicker.trackPreload(image, this, key));
          this.mediaCache.set(key, fragment);
        }
        return this.mediaCache.get(key);
      }

      static pendingPreloads = new Set();

      static trackPreload(image, picker, key) {
        if (image.complete) return;
        const entry = { image, picker, key, wanted: false };
        entry.done = new Promise((resolve) => {
          entry.settle = () => {
            MiniVariantPicker.pendingPreloads.delete(entry);
            resolve();
          };
        });
        MiniVariantPicker.pendingPreloads.add(entry);
        image.addEventListener('load', entry.settle, { once: true });
        image.addEventListener('error', entry.settle, { once: true });
      }

      // The tapped size's image jumps the queue: it gets high priority and every other
      // still-downloading background preload is cancelled, then that card goes back to
      // the end of the preload queue so its images are fetched again afterwards.
      static priorityDownload = null;

      static prioritize(image) {
        image.fetchPriority = 'high';
        const requeue = new Set();
        const tapped = [...MiniVariantPicker.pendingPreloads].find((entry) => entry.image === image) || {};
        [...MiniVariantPicker.pendingPreloads].forEach((entry) => {
          // The tapped image and the other images of the same swap (its hover/box image)
          // are kept; only other cards' background downloads are cancelled.
          if (entry.picker === tapped.picker && entry.key === tapped.key) entry.wanted = true;
          if (entry.image === image) {
            const download = entry.done.then(() => {
              if (MiniVariantPicker.priorityDownload === download) MiniVariantPicker.priorityDownload = null;
            });
            MiniVariantPicker.priorityDownload = download;
            return;
          }
          if (entry.wanted || entry.image.complete) return;
          entry.picker.mediaCache.delete(entry.key);
          entry.image.removeAttribute('srcset');
          entry.image.removeAttribute('src');
          entry.settle();
          requeue.add(entry.picker);
        });
        requeue.forEach((picker) => MiniVariantPicker.enqueuePreload(picker));
      }

      async swapMedia(variantId, url) {
        const card = this.mediaTarget.closest('.product-card');
        card?.querySelectorAll('a.product-card__media, a.product-title').forEach((link) => (link.href = url));

        const template = this.getMediaTemplate(variantId);
        if (!template || template.dataset.mediaKey === this.currentMediaKey) {
          this.swapToken++;
          return;
        }

        const token = ++this.swapToken;
        const key = template.dataset.mediaKey;
        const fragment = this.getCachedMedia(template);

        // Never show an empty box: the current image stays until the new one is fully
        // downloaded and decoded (a failed image is swapped in anyway after 8s at most).
        const image = fragment.querySelector('img');
        if (image && !(image.complete && image.naturalWidth > 0)) {
          MiniVariantPicker.prioritize(image);
          await Promise.race([
            image.decode().catch(
              () => new Promise((resolve) => (image.complete ? resolve() : image.addEventListener('load', resolve, { once: true })))
            ),
            new Promise((resolve) => setTimeout(resolve, 8000)),
          ]);
        }
        if (token !== this.swapToken) return;

        const outgoing = document.createDocumentFragment();
        outgoing.append(...this.mediaTarget.childNodes);
        this.mediaCache.set(this.currentMediaKey, outgoing);
        this.mediaCache.delete(key);

        this.mediaTarget.replaceChildren(fragment);
        this.currentMediaKey = key;
      }

      onVariantChange(event) {
        const radio = event.target;
        if (!radio.matches('.mini-variant-picker__radio')) return;

        const variantId = radio.value;
        const available = radio.dataset.available === 'true';

        this.variantIdInput.value = variantId;
        this.variantIdInput.disabled = !available;

        const priceTemplate = this.querySelector(`[data-variant-price-id="${variantId}"]`);
        if (priceTemplate && this.priceTarget) {
          this.priceTarget.innerHTML = priceTemplate.innerHTML;
        }

        const badgeTemplate = this.querySelector(`[data-variant-badge-id="${variantId}"]`);
        if (badgeTemplate && this.badgeTarget) {
          this.badgeTarget.innerHTML = badgeTemplate.innerHTML;
        }

        if (this.mediaTarget) this.swapMedia(variantId, radio.dataset.url);

        this.submitButton.disabled = !available;
        this.submitButton.removeAttribute('aria-disabled');
        if (this.submitButtonText) this.submitButtonText.classList.toggle('hidden', !available);
        if (this.soldOutMessage) this.soldOutMessage.classList.toggle('hidden', available);
      }
    }
  );
}
